/**
 * @module filterStoreHelpers
 * @description Shared helper functions for creating Pinia filter store state, getters, and actions.
 * Provides reusable filter logic for distance/state location filters, search, sorting,
 * job type filtering, pagination, Mixpanel tracking, and saved search management.
 */

import { storeToRefs } from 'pinia';
import { useJobsStore } from '@/stores/useJobs';
import { useMixPanelStore } from '@/stores/useMixPanelStore';
import { optimizeJobPhotoUrls } from '@/composables/useOptimizedPhotoUrl';

import { isAbortError } from '@/utilities/isAbortError';

const { addRumError, addRumAction } = useAcvDatadog();

/** @type {string|null} Cached postal code to avoid repeated sessionStorage reads */
let cachedPostalCode = null;

/**
 * Retrieves the default postal code from sessionStorage.
 * Caches the result to avoid repeated sessionStorage reads.
 * @returns {string|null} The cached postal code or null if not set
 */
const getDefaultPostalCode = () => {
  if (cachedPostalCode === null) {
    cachedPostalCode = JSON.parse(sessionStorage.getItem('postalCode'));
  }
  return cachedPostalCode;
};

/**
 * Creates a default distance filter state object.
 * @param {'pickup'|'delivery'} type - The location type (pickup or delivery)
 * @param {string} [id] - Unique identifier for the filter row; defaults to `{type}-{timestamp}`
 * @param {string} [postCode] - Postal code; defaults to the cached sessionStorage value
 * @param {number|string} [distance] - Distance value; defaults to 0
 * @returns {{ postCode: string|null, distance: number|string, type: string, id: string, filterType: string }}
 */
export const defineDefaultDistanceState = (type, id, postCode, distance) => {
  return {
    postCode: postCode || getDefaultPostalCode(),
    distance: distance || 0,
    type: type,
    id: id || `${type}-${Date.now()}`,
    filterType: 'distances',
  };
};

/**
 * Creates the base filter state for a filter store, with sensible defaults.
 * Any provided options are merged on top of the defaults.
 * @param {Object} [options={}] - Override values to merge into the default state (e.g. sort configuration)
 * @returns {Object} The initial filter state containing location, search, jobTypes, sort, filters, and previousSort
 */
export const createBaseFilterState = (options = {}) => ({
  location: {
    pickup: {
      distances: [defineDefaultDistanceState('pickup')],
      states: [],
    },
    delivery: {
      distances: [defineDefaultDistanceState('delivery')],
      states: [],
    },
  },
  search: [],
  jobTypes: [],
  previousSort: {
    text: null,
    value: null,
    order: null,
  },
  filters: {
    deliveryPastDue: false,
    pickupPastDue: false,
    deliverToday: false,
    pickupToday: false,
    bolNeeded: false,
  },
  sort: {}, // ensure there's a default sort object in the case one isn't passed in with the options object
  ...options,
});

/**
 * Creates the shared Pinia getter definitions for filter stores.
 * Includes getters for applied filters by type (pickup/delivery, distance/state),
 * combined filters, search text, sub-filters, bulk filters, and job type filters.
 * @returns {Object} An object of Pinia getter definitions
 */
export const createFilterGetters = () => ({
  appliedDeliveryStateFilters: (state) => state.location.delivery.states,
  appliedPickupStateFilters: (state) => state.location.pickup.states,
  appliedPickupDistanceFilters: (state) =>
    state.location.pickup.distances.filter((row) => row.distance.length > 0),
  appliedDeliveryDistanceFilters: (state) =>
    state.location.delivery.distances.filter((row) => row.distance.length > 0),
  appliedDeliveryFilters() {
    return this.appliedDeliveryDistanceFilters.concat(this.appliedDeliveryStateFilters);
  },
  appliedPickupFilters() {
    return this.appliedPickupDistanceFilters.concat(this.appliedPickupStateFilters);
  },
  appliedFilters() {
    return this.appliedPickupFilters
      .concat(this.appliedDeliveryFilters)
      .concat(this.search)
      .concat(this.appliedJobTypeFilters);
  },
  appliedBulkFilters: (state) => Object.values(state.filters).filter(Boolean),
  searchText: (state) => state.search?.[0]?.value ?? null,
  subFilters: (state) => state.filters,
  appliedJobTypeFilters: (state) => state.jobTypes,
});

/**
 * Creates the shared Pinia action definitions for filter stores.
 * Includes actions for managing filters, sorting, searching, pagination,
 * fetching filtered jobs, Mixpanel tracking, and saved searches.
 * @param {string} filterAPI - The API endpoint URL used to fetch filtered jobs
 * @param {Object} [options={}] - Configuration options (e.g. `sortObj` for default sort)
 * @param {boolean} [trackMixpanel=true] - Whether to send Mixpanel events from this store
 * @returns {Object} An object of Pinia action definitions
 */
export const createFilterActions = (filterAPI, options = {}, trackMixpanel = true) => ({
  /** Resets all filters to their initial state and tracks the event in Mixpanel. */
  resetAllFilters() {
    this.$reset();
    this.updateMixpanelProperties('clear all filters clicked');
  },

  /**
   * Updates a single filter by name.
   * @param {string} name - The filter property name (e.g. 'deliveryPastDue', 'bolNeeded')
   * @param {boolean} value - The new filter value
   */
  updateFilters(name, value) {
    this.filters = { ...this.filters, [name]: value };
  },

  /**
   * Replaces the entire filters object with the provided filters.
   * @param {Object} filters - The new filters object
   */
  updateBulkFilters(filters) {
    this.filters = filters;
  },

  /**
   * Adds a new empty distance filter row for the given location type.
   * @param {'pickup'|'delivery'} type - The location type
   */
  pushNewDistanceRow(type) {
    this.$patch((state) => {
      state.location[type].distances.push(defineDefaultDistanceState(type));
    });
  },

  /**
   * Removes a distance filter row at the specified index.
   * @param {'pickup'|'delivery'} type - The location type
   * @param {number} rowIndex - The index of the distance row to remove
   */
  removeDistanceRow(type, rowIndex) {
    if (rowIndex >= 0) {
      this.location[type].distances.splice(rowIndex, 1);
    }
  },

  /**
   * Resets all distance and state filters for the given location type.
   * @param {'pickup'|'delivery'} type - The location type to reset
   */
  resetFiltersByType(type) {
    this.location[type].distances = [];
    this.location[type].states = [];
    this.updateMixpanelProperties(`clear all ${type} filters clicked`);
  },

  /**
   * Sets a distance filter row with the provided form data at the specified index.
   * @param {'pickup'|'delivery'} type - The location type
   * @param {string} filterType - The filter category (e.g. 'distances')
   * @param {string} id - Unique identifier for the filter row
   * @param {{ postCode: string, distance: number|string }} formData - The form data containing postCode and distance
   * @param {number} $index - The index of the row to update
   */
  setDistanceRow(type, filterType, id, formData, $index) {
    this.location[type][filterType][$index] = defineDefaultDistanceState(
      type,
      id,
      formData.postCode,
      formData.distance,
      $index,
    );
    this.updateMixpanelProperties(`${type} distance jobs filtered`);
  },

  /**
   * Removes an applied distance filter by its ID. If no distance filters remain,
   * a default distance row is re-added.
   * @param {'pickup'|'delivery'} type - The location type
   * @param {string} id - The unique identifier of the distance filter to remove
   */
  removeAppliedDistanceFilter(type, id) {
    this.location[type].distances = this.location[type].distances.filter((item) => item.id !== id);

    if (this.location[type].distances.length === 0) {
      this.location[type].distances = [defineDefaultDistanceState(type)];
    }

    this.updateMixpanelProperties(`remove ${type} distance jobs filter`);
  },

  /**
   * Removes an applied state filter by its ID.
   * @param {'pickup'|'delivery'} type - The location type
   * @param {string} id - The unique identifier of the state filter to remove
   */
  removeAppliedStateFilter(type, id) {
    this.location[type].states = this.location[type].states.filter((item) => item.id !== id);
    this.updateMixpanelProperties(`remove ${type} state jobs filter`);
  },

  /**
   * Sets the active search query, replacing any existing search.
   * @param {Object} query - The search query object
   */
  pushNewSearchQuery(query) {
    this.search = [query];
    this.updateMixpanelProperties('jobs searched');
  },

  /** Clears the current search filter. */
  removeAppliedSearchFilter() {
    this.search = [];
    this.updateMixpanelProperties('jobs searched');
  },

  /**
   * Sets the state filters for a given location type.
   * @param {'pickup'|'delivery'} type - The location type
   * @param {Array<Object>} savedStates - Array of state filter objects to apply
   */
  setStateFilters(type, savedStates) {
    this.location[type].states = savedStates;
    this.updateMixpanelProperties(`${type} state jobs filtered`);
  },

  /**
   * Sets the job type filters.
   * @param {Array<Object>} selectedJobTypes - Array of selected job type filter objects
   */
  setJobTypeFilters(selectedJobTypes) {
    this.jobTypes = selectedJobTypes;
    this.updateMixpanelProperties('job types filtered');
  },

  /**
   * Updates the sort parameters, storing the previous sort for Mixpanel tracking.
   * @param {{ text: string, value: string, order: string }|null} sortObj - The new sort configuration
   */
  updateSortParams(sortObj) {
    if (sortObj) {
      this.previousSort = this.sort;
      this.sort = sortObj;
      this.updateMixpanelProperties('job sorted');
    }
  },

  /** Clears all job type filters. */
  clearJobTypeFilters() {
    this.jobTypes = [];
  },

  /**
   * Removes a specific job type filter by its ID.
   * @param {string} id - The unique identifier of the job type filter to remove
   */
  removeAppliedJobFilter(id) {
    this.jobTypes = this.jobTypes.filter((item) => item.id !== id);
  },

  /**
   * Fetches filtered jobs from the API using the current filter state.
   * Cancels any in-flight requests before making a new one.
   * Updates the jobs store with the response data.
   * @param {'append'|'replace'} style - Whether to append results to existing jobs or replace them
   * @returns {Promise<void>}
   */
  async fetchFilteredJobs(style) {
    const { $api, $cancelAllRequests } = useNuxtApp();
    $cancelAllRequests('New filter criteria applied');

    const jobsStore = useJobsStore();
    const { jobs, pagination, isPending, currentPageIndex, rowsPerPage } = storeToRefs(jobsStore);

    const body = {
      location: {
        pickup: {
          distances: this.appliedPickupDistanceFilters,
          states: this.appliedPickupStateFilters,
        },
        delivery: {
          distances: this.appliedDeliveryDistanceFilters,
          states: this.appliedDeliveryStateFilters,
        },
      },
      search: this.search?.length > 0 ? this.search : null,
      jobTypes: this.appliedJobTypeFilters,
      ...this.filters,
    };

    const params = {
      page: currentPageIndex.value,
      size: rowsPerPage.value,
      sort: `${[this.sort.value, this.sort.order]}`,
    };

    const ddContext = {
      style,
      filterAPI,
      body,
      params,
    };

    isPending.value = true;

    try {
      const response = await $api(filterAPI, {
        method: 'POST',
        body,
        params,
      });

      if (!response?.data?.content) {
        addRumAction('Fetch filtered jobs return no results', ddContext);
      }

      const newJobs = optimizeJobPhotoUrls(response.data.content);
      jobs.value = style === 'append' ? [...jobs.value, ...newJobs] : newJobs;
      pagination.value = response.data.pagination;
    } catch (error) {
      if (isAbortError(error)) {
        addRumAction('Fetch filtered jobs aborted', {
          ...ddContext,
          reason: error.cause || error.message,
        });
      } else if (error.response?.status >= 400) {
        addRumError(error, {
          ...ddContext,
          actionName: 'Failed to fetch filtered jobs',
          currentJobsCount: jobs.value?.length,
          pageIndex: currentPageIndex.value,
        });
      } else {
        addRumAction('Fetch filtered jobs network failure', {
          ...ddContext,
          reason: error.message,
        });
      }
    } finally {
      isPending.value = false;
    }
  },

  /**
   * Resets pagination to the first page by trimming the jobs list
   * to the first 50 items and setting the page index to 0.
   */
  setToFirstPage() {
    const jobsStore = useJobsStore();
    const { jobs, currentPageIndex } = storeToRefs(jobsStore);
    jobs.value = jobs.value.slice(0, 50);
    currentPageIndex.value = 0;
  },

  /**
   * Sends current filter, sort, and search state to Mixpanel.
   * Skipped when operating within a saved search context.
   * @param {string} eventName - The Mixpanel event name to track
   */
  updateMixpanelProperties(eventName) {
    if (!trackMixpanel) return;

    const mixPanelStore = useMixPanelStore();
    mixPanelStore.setSearchText(this.searchText);
    mixPanelStore.setSortOptions({
      previousSortByDirection: this.previousSort.order,
      previousSortByField: this.previousSort.text,
      sortByDirection: this.sort.order,
      sortByField: this.sort.text,
    });
    mixPanelStore.setFilterOptions({
      pickupStates: this.appliedPickupStateFilters.length ? this.appliedPickupStateFilters : null,
      deliveryStates: this.appliedDeliveryStateFilters.length
        ? this.appliedDeliveryStateFilters
        : null,
      pickupZipRanges: this.appliedPickupDistanceFilters.length
        ? this.appliedPickupDistanceFilters
        : null,
      deliveryZipRanges: this.appliedDeliveryDistanceFilters.length
        ? this.appliedDeliveryDistanceFilters
        : null,
      jobTypes: this.jobTypes.length ? this.jobTypes : null,
    });

    mixPanelStore.fetchMixPanel(eventName);
  },

  /** Resets all bulk filters to false and restores the default sort from options. */
  resetBulkFiltersAndSort() {
    this.$patch({
      filters: {
        deliveryPastDue: false,
        pickupPastDue: false,
        deliverToday: false,
        pickupToday: false,
        bolNeeded: false,
      },
      sort: options.sort,
    });
  },

  /**
   * Displays a snackbar notification message.
   * @param {string} message - The message to display in the snackbar
   */
  showSnackBarMessage(message) {
    this.snackBarMessage = message;
  },

  /** Clears the current snackbar message. */
  resetSnackBarMessage() {
    this.snackBarMessage = null;
  },
});
